import {
	always,
	divide,
	range,
} from 'ramda';

const emptyArray = always([])

/**
 * @typedef PagerProps
 * @property { number } value
 * @property { boolean } active
 */

/** @type { import('vue/types/options').FunctionalComponentOptions<PagerProps> } */
const Pager = {
	functional: true,
	render(h, { props, listeners }) {
		return (
			<button
				class={{
					'pagination__item' : true,
					'pagination__item_active': props.active,
				}}
				onClick={ () => listeners.click?.(props.value) }
			>
				{ props.value }
			</button>
		)
	},
}

/**
 * @typedef PaginationProps
 * @property { number } current
 * @property { number? } total
 * @property { number? } pageSize
 * @property { number[] } sortArray
 */

/** @type { import('vue/types/options').FunctionalComponentOptions<PaginationProps> } */
const Pagination = {
	functional: true,
	name: 'Pagination',
	props: {
		pageSize: {
			type: Number,
			default: null,
		},
		current: {
			type: Number,
			default: null,
		},
		total: {
			type: Number,
			default: 0,
		},
		sortArray: {
			type: Array,
			default: emptyArray,
		},
	},
	render(h, { props, listeners }) {
		const { total, current, pageSize, sortArray } = props;

		let maxPages = Math.ceil(total / pageSize);
		const buffer = 2;
		let left = Math.max(1, current - buffer);
		let right = Math.min(current + buffer, maxPages);

		if (!(maxPages <= 3 + buffer * 2)) {
			if (current - 1 <= buffer)
				right = 1 + buffer * 2;

			if (maxPages - current <= buffer)
				left = maxPages - buffer * 2;
		}

		const pages = range(left, right + 1);

		let newCount = (value) => Math.trunc(
			divide((current - 1) * pageSize + 1, value)
		) + 1;

		/**
		 * Trigger `change` listener
		 *
		 * @param {number} value
		 * @returns
		 */
		const change = (value) => listeners.change?.(value, pageSize)

		return (
			<div class="category-products__pagination">
				<div class="pagination">
					<div class="pagination__content">
						{ current !== 1 ? (
							<button
								class="pagination__item pagination__item_back"
								onClick={ () => change(current - 1) }
							>
								<svg class="pagination__item-arrow" viewBox="0 0 6 11">
									<path d="M0.207884 10.7932C0.270288 10.8588 0.344393 10.9108 0.425963 10.9463C0.507533 10.9817 0.594967 11 0.683269 11C0.771569 11 0.859004 10.9817 0.940574 10.9463C1.02214 10.9108 1.09625 10.8588 1.15865 10.7932L5.72603 6.00485C5.7885 5.93938 5.83806 5.86163 5.87188 5.77605C5.90569 5.69047 5.9231 5.59874 5.9231 5.50609C5.9231 5.41345 5.90569 5.32172 5.87188 5.23614C5.83806 5.15056 5.7885 5.07281 5.72603 5.00734L1.15865 0.218941C1.09682 0.150784 1.02261 0.0963035 0.940396 0.0587097C0.85818 0.0211158 0.769623 0.00117066 0.679944 5.00864e-05C0.590266 -0.00107049 0.50128 0.0166554 0.418234 0.0521836C0.335189 0.0877118 0.259763 0.140324 0.196403 0.206917C0.133042 0.27351 0.0830288 0.352737 0.0493121 0.439928C0.0155954 0.527119 -0.00114302 0.620511 8.38688e-05 0.714595C0.00131076 0.808681 0.0204767 0.901556 0.0564541 0.987748C0.0924315 1.07394 0.144492 1.1517 0.209566 1.21645L4.30156 5.50609L0.209565 9.79574C0.146981 9.86109 0.0972897 9.93875 0.0633317 10.0243C0.0293737 10.1098 0.0118142 10.2015 0.0116583 10.2941C0.0115023 10.3868 0.0287533 10.4785 0.0624233 10.5642C0.0960927 10.6498 0.145522 10.7277 0.207884 10.7932Z" />
								</svg>
								Назад
							</button>
						) : null }

						{ pages[0] !== 1 ? (
							<span>
								<Pager
									value={ 1 }
									onClick={ change }
								/>
								<span style={{ 'white-space': 'pre' }}> ... </span>
							</span>
						) : null }

						{ total > 0 && !isNaN(maxPages) ? (
							pages.map((value) => (
								<Pager
									value={ value }
									active={ value === current }
									onClick={ change }
								/>
							))
						) : null }

						{ pages[pages.length - 1] !== maxPages ? (
							<span>
								<span style={{ 'white-space': 'pre' }}> ... </span>
								<Pager
									value={ maxPages }
									onClick={ change }
								/>
							</span>
						) : null }

						{ maxPages !== current ? (
							<button
								class="pagination__item pagination__item_forward"
								onClick={ () => change(current + 1) }
							>
								Вперед
								<svg class="pagination__item-arrow" viewBox="0 0 6 11">
									<path d="M0.207884 10.7932C0.270288 10.8588 0.344393 10.9108 0.425963 10.9463C0.507533 10.9817 0.594967 11 0.683269 11C0.771569 11 0.859004 10.9817 0.940574 10.9463C1.02214 10.9108 1.09625 10.8588 1.15865 10.7932L5.72603 6.00485C5.7885 5.93938 5.83806 5.86163 5.87188 5.77605C5.90569 5.69047 5.9231 5.59874 5.9231 5.50609C5.9231 5.41345 5.90569 5.32172 5.87188 5.23614C5.83806 5.15056 5.7885 5.07281 5.72603 5.00734L1.15865 0.218941C1.09682 0.150784 1.02261 0.0963035 0.940396 0.0587097C0.85818 0.0211158 0.769623 0.00117066 0.679944 5.00864e-05C0.590266 -0.00107049 0.50128 0.0166554 0.418234 0.0521836C0.335189 0.0877118 0.259763 0.140324 0.196403 0.206917C0.133042 0.27351 0.0830288 0.352737 0.0493121 0.439928C0.0155954 0.527119 -0.00114302 0.620511 8.38688e-05 0.714595C0.00131076 0.808681 0.0204767 0.901556 0.0564541 0.987748C0.0924315 1.07394 0.144492 1.1517 0.209566 1.21645L4.30156 5.50609L0.209565 9.79574C0.146981 9.86109 0.0972897 9.93875 0.0633317 10.0243C0.0293737 10.1098 0.0118142 10.2015 0.0116583 10.2941C0.0115023 10.3868 0.0287533 10.4785 0.0624233 10.5642C0.0960927 10.6498 0.145522 10.7277 0.207884 10.7932Z" />
								</svg>
							</button>
						) : null }
					</div>
				</div>
				<div class="per-page_block">
					<p class="m-0 per-page_content">
						<span>показать по:</span>
						{ sortArray.map((value) => (
							<button
								class={{
									'font-bold': pageSize === value,
								}}
								onClick={ () => listeners.change(newCount(value), value) }
							>
									{value}
							</button>
						)) }
					</p>
				</div>
			</div>
		);
	}
}

export default Pagination;
